/** @jsx jsx */
import { jsx } from "theme-ui"
import React from "react"
import { navigate, PageRenderer, Link } from "gatsby"
import { Global } from "@emotion/core"
import mousetrap from "mousetrap"
import { MdClose } from "react-icons/md"
import LazyModal from "./lazy-modal"
import { globalStyles } from "../utils/styles/global"
import { hexToRGBA, useTheme } from "gatsby-interface"

export default function Modal({
  modalBackgroundPath,
  children,
  next,
  previous,
  filters,
  modalPreviousLink,
  modalNextLink,
}) {
  React.useEffect(() => {
    document.querySelector(`html`).style.overflowY = `hidden`

    return () => {
      document.querySelector(`html`).style.overflowY = `auto`
    }
  }, [])

  function modalPrev() {
    navigate(previous, {
      state: { isModal: true, filters },
    })
  }

  function modalNext() {
    navigate(next, {
      state: { isModal: true, filters },
    })
  }

  React.useEffect(() => {
    mousetrap.bind(`left`, modalPrev)
    mousetrap.bind(`right`, modalNext)
    mousetrap.bind(`spacebar`, modalNext)

    return () => {
      mousetrap.unbind(`left`)
      mousetrap.unbind(`right`)
      mousetrap.unbind(`spacebar`)
    }
  }, [previous, next])

  const theme = useTheme()

  return (
    <>
      <Global styles={globalStyles} />
      <PageRenderer location={{ pathname: modalBackgroundPath }} />
      <LazyModal
        isOpen={true}
        style={{
          content: {
            background: `none`,
            border: `none`,
            bottom: `inherit`,
            left: `inherit`,
            margin: `0 auto`,
            overflow: `visible`,
            padding: `${theme.space[9]} 0`,
            right: `inherit`,
            top: `inherit`,
            maxWidth: `1050px`,
          },
          overlay: {
            backgroundColor: hexToRGBA(theme.colors.white, 0.95, true),
            bottom: `unset`,
            left: 0,
            minHeight: `100%`,
            minWidth: `100%`,
            overflowY: `auto`,
            position: `absolute`,
            right: 0,
            top: 0,
            zIndex: 1001, // this needs to be larger than the zIndex for the header
          },
        }}
        onRequestClose={() => navigate(modalBackgroundPath)}
        contentLabel="Site Details Modal"
      >
        <div
          sx={t => ({
            display: `flex`,
            flexWrap: `wrap`,
            justifyContent: `space-between`,
            [t.mediaQueries.tablet]: {
              flexWrap: `nowrap`,
            },
          })}
        >
          <div
            sx={{
              bg: `white`,
              borderRadius: 2,
              boxShadow: `dialog`,
              position: `relative`,
              alignItems: `center`,
              order: 1,
              width: `100%`,
            }}
          >
            <button
              onClick={() => navigate(modalBackgroundPath)}
              sx={{
                bg: `white`,
                border: 0,
                borderRadius: 6,
                color: `text.secondary`,
                cursor: `pointer`,
                fontSize: 4,
                height: 40,
                left: `auto`,
                position: `absolute`,
                right: 8,
                top: 9,
                width: 40,
                "&:hover": {
                  bg: `purple.5`,
                  color: `gatsby`,
                },
              }}
            >
              <MdClose />
            </button>
            {children}
          </div>
          <Link
            to={previous}
            state={{
              isModal: true,
              filters,
            }}
            sx={t => ({
              order: 1,
              position: `relative`,
              width: `100%`,
              m: 7,
              display: `flex`,
              [t.mediaQueries.tablet]: {
                display: `block`,
                order: 0,
                top: `110px`,
                width: `auto`,
              },
            })}
          >
            {modalPreviousLink}
          </Link>
          <Link
            to={next}
            state={{
              isModal: true,
              filters,
            }}
            sx={t => ({
              order: 2,
              m: 7,
              mt: 0,
              position: `relative`,
              display: `flex`,
              [t.mediaQueries.tablet]: {
                mt: 7,
                display: `block`,
                top: `110px`,
              },
            })}
          >
            {modalNextLink}
          </Link>
        </div>
      </LazyModal>
    </>
  )
}
